import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  addJobMaterial,
  completeJob,
  createJobVisit,
  deleteJobMaterial,
  deleteJobVisit,
  getJob,
  listJobFiles,
  listTeamMembers,
  updateJob,
  updateJobVisit,
} from "../../api/jobsApi";
import { normalizeArray } from "../../apiClient";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import "./Jobs.css";

const money = (v) =>
  v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const memberName = (m) =>
  [m.first_name, m.last_name].filter(Boolean).join(" ").trim() || m.name || m.email || `#${m.id}`;

export default function JobDetailPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [state, setState] = useState("loading");
  const [team, setTeam] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  // Instructions draft
  const [instructions, setInstructions] = useState("");
  const [instructionsDirty, setInstructionsDirty] = useState(false);

  // New material draft
  const [matName, setMatName] = useState("");
  const [matQty, setMatQty] = useState("1");
  const [matCost, setMatCost] = useState("");

  // New visit draft
  const [visitDate, setVisitDate] = useState("");
  const [visitStart, setVisitStart] = useState("08:00");
  const [visitHours, setVisitHours] = useState("8");
  const [visitAssignee, setVisitAssignee] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await getJob(jobId);
      setJob(data);
      setInstructions(data.instructions || "");
      setInstructionsDirty(false);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await listTeamMembers();
        setTeam(normalizeArray(data));
      } catch {
        setTeam([]);
      }
      try {
        const { data } = await listJobFiles(jobId);
        setPhotos(normalizeArray(data));
      } catch {
        setPhotos([]);
      }
    })();
  }, [jobId]);

  const openVisits = useMemo(
    () => (job?.visits || []).filter((v) => !["canceled", "cancelled"].includes(v.status)),
    [job]
  );

  if (state === "loading") return <div className="jobs-page"><div className="jobs-empty">Loading job…</div></div>;
  if (state === "error" || !job)
    return <div className="jobs-page"><div className="jobs-empty">Couldn’t load this job — try again.</div></div>;

  const saveInstructions = async () => {
    setBusy(true);
    try {
      await updateJob(job.id, { instructions });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const addMaterial = async (e) => {
    e.preventDefault();
    if (!matName.trim()) return;
    setBusy(true);
    try {
      await addJobMaterial(job.id, {
        name: matName.trim(),
        quantity: matQty || "1",
        unit_cost: matCost || "0",
        source: "office",
      });
      setMatName("");
      setMatQty("1");
      setMatCost("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const removeMaterial = async (materialId) => {
    setBusy(true);
    try {
      await deleteJobMaterial(job.id, materialId);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const addVisit = async (e) => {
    e.preventDefault();
    if (!visitDate) return;
    setBusy(true);
    try {
      const start = new Date(`${visitDate}T${visitStart || "08:00"}:00`);
      await createJobVisit({
        job: job.id,
        customer: job.customer,
        start: start.toISOString(),
        duration_minutes: Math.round(Number(visitHours || 8) * 60),
        assigned_user: visitAssignee || null,
        service_type: job.title,
        address: job.address,
        city: job.city,
        state: job.state,
        zip_code: job.zip_code,
      });
      setVisitDate("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const assignVisit = async (visitId, userId) => {
    setBusy(true);
    try {
      await updateJobVisit(visitId, { assigned_to: userId || null });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const removeVisit = async (visitId) => {
    setBusy(true);
    try {
      await deleteJobVisit(visitId);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const markComplete = async () => {
    setBusy(true);
    try {
      await completeJob(job.id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="jobs-page">
      <Card>
        <CardHeader
          title={`${job.job_number} — ${job.title}`}
          subtitle={
            <>
              {job.customer ? (
                <Link to={`/customers/${job.customer}`} className="jobs-link">
                  {job.customer_name || "Customer"}
                </Link>
              ) : (
                job.customer_name || "No customer"
              )}
              {job.address ? ` · ${job.address}${job.city ? `, ${job.city}` : ""}` : ""}
              {job.quote ? (
                <>
                  {" · "}
                  <Link to={`/quotes/${job.quote}`} className="jobs-link">
                    quote{job.quote_status ? ` (${job.quote_status})` : ""}
                  </Link>
                </>
              ) : null}
            </>
          }
          actions={
            job.status === "completed" ? (
              <span className="jobs-status jobs-status--completed">
                Completed
                {job.completed_at
                  ? ` ${new Date(job.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                  : ""}
              </span>
            ) : (
              <button className="jobs-btn jobs-btn--primary" disabled={busy} onClick={markComplete}>
                Mark job complete
              </button>
            )
          }
        />
        <CardBody>
          {/* ── Price vs cost ── */}
          <div className="jobs-financials">
            <div className="jobs-fin">
              <span className="jobs-fin__label">Quote price</span>
              <span className="jobs-fin__value">{money(job.quote_price)}</span>
            </div>
            <div className="jobs-fin">
              <span className="jobs-fin__label">Materials cost</span>
              <span className="jobs-fin__value">{money(job.materials_cost)}</span>
            </div>
            <div className="jobs-fin">
              <span className="jobs-fin__label">Labor hours</span>
              <span className="jobs-fin__value">
                {job.labor_hours_completed} done / {job.labor_hours_scheduled} scheduled
                {job.estimated_labor_hours ? ` (est ${job.estimated_labor_hours})` : ""}
              </span>
            </div>
            <div className="jobs-fin jobs-fin--margin">
              <span className="jobs-fin__label">Running margin</span>
              <span className="jobs-fin__value">{money(job.running_margin)}</span>
            </div>
          </div>

          {/* ── Instructions ── */}
          <Card nested>
            <CardHeader
              title="Instructions"
              subtitle="What the crew reads on their phone — access notes, gate codes, scope."
              actions={
                instructionsDirty ? (
                  <button className="jobs-btn jobs-btn--primary" disabled={busy} onClick={saveInstructions}>
                    Save
                  </button>
                ) : null
              }
            />
            <CardBody>
              <textarea
                className="jobs-instructions"
                rows={4}
                placeholder="e.g. Gate code 4421. Park on the street — sprinklers run until 8am."
                value={instructions}
                onChange={(e) => {
                  setInstructions(e.target.value);
                  setInstructionsDirty(true);
                }}
              />
            </CardBody>
          </Card>

          {/* ── Visits ── */}
          <Card nested>
            <CardHeader title="Visits" subtitle="The scheduled days on this job and who's on them." />
            <CardBody flush>
              {openVisits.length === 0 && <div className="jobs-empty">No visits scheduled yet.</div>}
              {openVisits.length > 0 && (
                <div className="jobs-table-wrap">
                  <table className="jobs-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Time</th>
                        <th>Assigned</th>
                        <th>Status</th>
                        <th>Notes from field</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {openVisits.map((v) => (
                        <tr key={v.id}>
                          <td>
                            {v.start_time
                              ? new Date(v.start_time).toLocaleDateString(undefined, {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "—"}
                          </td>
                          <td>
                            {v.start_time
                              ? `${new Date(v.start_time).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}–${new Date(v.end_time).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`
                              : "—"}
                          </td>
                          <td>
                            <select
                              className="jobs-filter"
                              value={v.assigned_to || ""}
                              disabled={busy}
                              onChange={(e) => assignVisit(v.id, e.target.value)}
                              aria-label="Assign visit"
                            >
                              <option value="">Unassigned</option>
                              {team.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {memberName(m)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <span className={`jobs-status jobs-status--${v.status}`}>{v.status}</span>
                          </td>
                          <td className="jobs-visit-notes">{v.notes || "—"}</td>
                          <td>
                            <button
                              className="jobs-btn jobs-btn--ghost"
                              disabled={busy}
                              onClick={() => removeVisit(v.id)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <form className="jobs-add-row" onSubmit={addVisit}>
                <input
                  type="date"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  aria-label="Visit date"
                  required
                />
                <input
                  type="time"
                  value={visitStart}
                  onChange={(e) => setVisitStart(e.target.value)}
                  aria-label="Start time"
                />
                <input
                  type="number"
                  min="1"
                  max="14"
                  step="0.5"
                  value={visitHours}
                  onChange={(e) => setVisitHours(e.target.value)}
                  aria-label="Hours"
                  title="Hours"
                />
                <select
                  value={visitAssignee}
                  onChange={(e) => setVisitAssignee(e.target.value)}
                  aria-label="Assign to"
                >
                  <option value="">Assign later</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>
                      {memberName(m)}
                    </option>
                  ))}
                </select>
                <button className="jobs-btn jobs-btn--primary" disabled={busy || !visitDate} type="submit">
                  Add visit
                </button>
              </form>
            </CardBody>
          </Card>

          {/* ── Materials ── */}
          <Card nested>
            <CardHeader
              title="Materials"
              subtitle="What the job costs you — quantity × unit cost. Quote items marked as materials arrive as stubs; enter real costs here."
            />
            <CardBody flush>
              {(job.materials || []).length > 0 && (
                <div className="jobs-table-wrap">
                  <table className="jobs-table">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>Qty</th>
                        <th>Unit cost</th>
                        <th>Total</th>
                        <th>Source</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {job.materials.map((m) => (
                        <tr key={m.id}>
                          <td>{m.name}</td>
                          <td>{Number(m.quantity)}</td>
                          <td>{money(m.unit_cost)}</td>
                          <td>{money(m.total_cost)}</td>
                          <td>{m.source}</td>
                          <td>
                            <button
                              className="jobs-btn jobs-btn--ghost"
                              disabled={busy}
                              onClick={() => removeMaterial(m.id)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <form className="jobs-add-row" onSubmit={addMaterial}>
                <input
                  type="text"
                  placeholder="Material (e.g. Sod pallet)"
                  value={matName}
                  onChange={(e) => setMatName(e.target.value)}
                  aria-label="Material name"
                  required
                />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={matQty}
                  onChange={(e) => setMatQty(e.target.value)}
                  aria-label="Quantity"
                  title="Quantity"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Unit cost"
                  value={matCost}
                  onChange={(e) => setMatCost(e.target.value)}
                  aria-label="Unit cost"
                />
                <button className="jobs-btn jobs-btn--primary" disabled={busy || !matName.trim()} type="submit">
                  Add material
                </button>
              </form>
            </CardBody>
          </Card>

          {/* ── Photos from the field ── */}
          <Card nested>
            <CardHeader title="Photos" subtitle="Uploaded from the field, attached to this job." />
            <CardBody>
              {photos.length === 0 ? (
                <div className="jobs-empty">No photos yet.</div>
              ) : (
                <div className="jobs-photos">
                  {photos.map((p) => (
                    <a key={p.id} href={p.url || p.file} target="_blank" rel="noreferrer" className="jobs-photo">
                      <img src={p.url || p.file} alt={p.name || "Job photo"} loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </CardBody>
      </Card>
    </div>
  );
}
